import React from 'react';
import { urlConfig } from '../config/urlConfig';
import appIntroImage from '../images/512.png';

function Home() {
  return (
    <>
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <span className="hero-tagline">For YouTube Creators</span>
          <div className="hero-title-wrapper">
            <img src="/favicon.png" alt="ForTuber Icon" className="hero-icon" />
            <h1 className="hero-title">ForTuber</h1>
          </div>
          <p className="hero-subtitle">AI-Powered YouTube Content Translation</p>
          <p className="hero-description">
            Empowering YouTube creators with intelligent multilingual translation
            to reach global audiences effortlessly.
          </p>
          {urlConfig.showAppDownloadLinks ? (
            <div className="store-badges">
              <a href={urlConfig.androidLink} target="_blank" rel="noopener noreferrer" className="store-badge" aria-label="Download on Google Play">
                <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Download on Google Play" />
              </a>
              <a href={urlConfig.iosLink} target="_blank" rel="noopener noreferrer" className="store-badge" aria-label="Download on the App Store">
                <img src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" alt="Download on the App Store" />
              </a>
            </div>
          ) : null}
          <div className="hero-company">
            <span className="company-label">by</span>
            <span className="company-name">DIDI</span>
          </div>
        </div>
      </section>

      {/* App Description Section */}
      <section className="app-description">
        <div className="container">
          <h2 className="section-title">What is ForTuber?</h2>
          <div className="app-intro-media">
            <img
              src={appIntroImage}
              alt="ForTuber app preview"
              className="app-intro-image"
              loading="lazy"
            />
          </div>
          <div className="app-intro">
            <div className="app-intro-icon">🚀</div>
            <p className="app-intro-text">
              ForTuber is an AI-powered app for YouTube creators
              {urlConfig.showAppDownloadLinks ? (
                <>, available on <strong> Google Play</strong> and <strong>App Store</strong>.</>
              ) : (
                <>.</>
              )}
            </p>
          </div>
          <div className="app-features-list">
            <div className="app-feature-item">
              <div className="app-feature-icon">🌍</div>
              <div className="app-feature-content">
                <h3>Go Global with Multilingual Translation</h3>
                <p>
                  Automatically translate your channel description, YouTube video metadata (titles and descriptions),
                  and video subtitles/captions into multiple languages. This feature helps you reach more international viewers
                  and globalize your content effortlessly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <h2 className="section-title">Key Features</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🌐</div>
              <h3 className="feature-title">Multilingual Translation</h3>
              <p className="feature-description">
                Automatically translate your channel description, video metadata (titles and descriptions),
                and video subtitles/captions into multiple languages using advanced AI technology.
                Reach global audiences without the hassle of manual translation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Data Access Section */}
      <section className="data-access">
        <div className="container">
          <h2 className="section-title">How We Use Your Data</h2>
          <div className="data-access-intro">
            <p>
              ForTuber requires <strong>Google Sign-In</strong> to access your YouTube channel. 
              Here's exactly what we do and don't do with your data.
            </p>
          </div>

          {/* What We Do */}
          <div className="data-section">
            <h3 className="data-section-title">
              <span className="data-section-icon">✅</span>
              What ForTuber Does
            </h3>
            <div className="data-grid">
              <div className="data-card data-card-do">
                <div className="data-card-header">
                  <span className="data-card-icon">🌐</span>
                  <h4>Translation</h4>
                </div>
                <ul>
                  <li>Read and add translated channel description</li>
                  <li>Read and add translated video metadata (titles, descriptions)</li>
                  <li>Read and add translated video subtitles/captions</li>
                </ul>
              </div>
              <div className="data-card data-card-do">
                <div className="data-card-header">
                  <span className="data-card-icon">👤</span>
                  <h4>Account</h4>
                </div>
                <p>Google Sign-In supported</p>
              </div>
            </div>
          </div>

          {/* What We Don't Do */}
          <div className="data-section">
            <h3 className="data-section-title">
              <span className="data-section-icon">🚫</span>
              What ForTuber Never Does
            </h3>
            <div className="data-never-grid">
              <div className="data-never-item">
                <span className="never-icon">❌</span>
                <span>Delete your videos</span>
              </div>
              <div className="data-never-item">
                <span className="never-icon">❌</span>
                <span>Upload new videos</span>
              </div>
              <div className="data-never-item">
                <span className="never-icon">❌</span>
                <span>Modify your subscriptions</span>
              </div>
              <div className="data-never-item">
                <span className="never-icon">❌</span>
                <span>Change your playlists</span>
              </div>
              <div className="data-never-item">
                <span className="never-icon">❌</span>
                <span>Share your data with third parties</span>
              </div>
              <div className="data-never-item">
                <span className="never-icon">❌</span>
                <span>Access your private videos</span>
              </div>
            </div>
          </div>

          {/* Why Broad Permissions */}
          <div className="data-explanation">
            <div className="explanation-icon">ℹ️</div>
            <div className="explanation-content">
              <h4>Why does Google show broad permissions?</h4>
              <p>
                Google's permission system groups related actions together. While the permission may say 
                "view, edit, and delete," ForTuber only uses the specific features listed above. 
                We never perform destructive actions like deleting your content.
              </p>
            </div>
          </div>

          <div className="data-notice">
            <div className="notice-icon">🔒</div>
            <div className="notice-content">
              <h4>Privacy Protection Notice</h4>
              <p>
                ForTuber protects your data securely and uses it solely for service delivery purposes. 
                For more details, please review our <a href="/privacy">Privacy Policy</a>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about">
        <div className="container">
          <h2 className="section-title">About DIDI</h2>
          <div className="about-content">
            <p className="about-text">
              DIDI is committed to providing innovative solutions for content creators. 
              ForTuber represents our dedication to leveraging AI technology to help 
              YouTube creators overcome language barriers and enhance their global presence.
            </p>
            <p className="about-text">
              Our platform combines cutting-edge artificial intelligence with user-friendly 
              interfaces to deliver seamless translation tools that save time 
              and maximize your content's global reach.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
